"use client";

import { SectionHeader } from "@/components/common/section-header";
import { Hero } from "@/types/dto/hero/hero";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import { Box, Stack, alpha } from "@mui/material";
import Image from "next/image";
import React from "react";
import {BasicInfo, BasicInfoCta} from "@/types/dto/common/basic-info";

interface ExploreColletteProps {
  data: Hero;
  moduleRankingPosition?: number;
}

const ExploreCollette = ({ data, moduleRankingPosition }: ExploreColletteProps) => {
  const { backgroundImage, basicInfo } = data;

  const listOfCtas: BasicInfoCta[] | undefined =
    "ctas" in basicInfo && basicInfo.ctas
      ? basicInfo.ctas
      : undefined;

  const firstCta = listOfCtas?.[0];

  const singleCtaBasicInfo: BasicInfo | undefined =
    !("ctas" in basicInfo) || !basicInfo.ctas
      ? basicInfo
      : undefined;

  return (
    <Box
      data-module-name="Explore Collette"
      data-module-ranking={moduleRankingPosition}
      data-parent-container-type="awards-reviews-page"
      data-testid="explore-collette-container"
      data-track="visibility"
      component="section"
      className="fluid-box"
      sx={(theme) => ({
        "&::before": {
          content: "\"\"",
          position: "absolute",
          inset: 0,
          background: {
            xs: `linear-gradient(90deg, ${alpha(theme.palette.common.black, 0.6)} 1.29%, ${alpha(theme.palette.common.black, 0.46)} 15.52%, ${alpha(theme.palette.common.black, 0.13)} 86.96%, ${alpha(theme.palette.common.black, 0)} 99.9%)`,
            sm: `linear-gradient(90deg, ${alpha(theme.palette.common.black, 0.6)} 1.29%, ${alpha(theme.palette.common.black, 0.46)} 15.52%, ${alpha(theme.palette.common.black, 0.13)} 59.47%, ${alpha(theme.palette.common.black, 0)} 80.63%)`,
            md: `linear-gradient(90deg, ${alpha(theme.palette.common.black, 0.6)} 1.29%, ${alpha(theme.palette.common.black, 0.46)} 15.52%, ${alpha(theme.palette.common.black, 0.13)} 59.47%, ${alpha(theme.palette.common.black, 0)} 80.63%)`,
            lg: `linear-gradient(90deg, ${alpha(theme.palette.common.black, 0.6)} 1.29%, ${alpha(theme.palette.common.black, 0.46)} 21.04%, ${alpha(theme.palette.common.black, 0.13)} 35.38%, ${alpha(theme.palette.common.black, 0.05)} 40.25%, ${alpha(theme.palette.common.black, 0)} 99.9%)`
          },
          zIndex: 1
        }
      })}
    >
      {backgroundImage?.altText && backgroundImage?.imageUrl && (
        <Image
          src={backgroundImage?.imageUrl}
          alt={backgroundImage?.altText}
          fill
          style={{ objectFit: "cover" }}
          data-testid="explore-collette-background-image"
        />
      )}
      <Box
        className="full-width-container"
        data-testid="explore-collette-section"
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "25rem", md: "30rem", lg: "60rem" }
        }}
      >
        <Stack
          spacing="2.4rem"
          sx={{
            position: "absolute",
            inset: 0,
            p: { xs: "3.2rem 2.4rem", md: "3.2rem", lg: "10rem" },
            zIndex: 2
          }}
        >
          <SectionHeader
            headerText={basicInfo?.headerText}
            headerSx={{
              textTransform: "none",
              fontSize: { xs: "2.4rem", md: "2.6rem", lg: "2.8rem" }
            }}
            sx={{ color: "common.white", gap: "0.8rem" }}
            headerTestId="explore-collette-header"
            copyTestId="explore-collette-copy"
            copyText={basicInfo?.copyText}
            copySx={{ "& p": { margin: 0 } }}
          />
          {}
          {(singleCtaBasicInfo || firstCta) && (
            <ColletteButton
              testId="explore-collette-link"
              label={singleCtaBasicInfo?.callToActionText || firstCta?.callToActionText || "Explore Collette Tours"}
              component="a"
              sx={{ alignSelf: "start" }}
              size="medium"
              color="secondary"
              href={singleCtaBasicInfo?.callToActionUrl ?? firstCta?.callToActionUrl}
            />
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default ExploreCollette;
